import EditNotificationsOutlinedIcon from "@mui/icons-material/EditNotificationsOutlined";
import MoodIcon from "@mui/icons-material/Mood";
import { type ReactNode } from "react";
import {
  Edit3 as ArticleIcon,
  Zap as AutomationIcon,
  Book as BookIcon,
  BookOpen as BookOpenIcon,
  Calendar as CalendarIcon,
  UserX as CancellationIcon,
  File as FileIcon,
  Film as FilmIcon,
  Home as HomeBanners,
  Image as ImageIcon,
  BarChart2 as ImpactIcon,
  Layout as LayoutIcon,
  Airplay as LiveIcon,
  MessageSquare as MessageSquareIcon,
  UserPlus as NewMemberIcon,
  Radio as OnlineIcon,
  AlertOctagon as ReportIcon,
  TrendingDown as RetentionIcon,
  Search as SearchIcon,
  Share2 as Share2Icon,
  Target as TargetIcon,
  Tool as ToolIcon,
  User as UserIcon,
  Users as UsersIcon,
} from "react-feather";

/**
 * Types
 */
export interface NavItem {
  icon: ReactNode;
  title: string;
  href: string;
  /** One line, shown on the home launcher. Says what the page is for. */
  description: string;
}

export interface NavGroup {
  title: string;
  list: NavItem[];
}

/**
 * Navigation
 *
 * The single source for both the sidebar and the home launcher, so the two can
 * never drift apart. Grouped the way the member tools group their own sidebar.
 *
 * This is the redesign foundation: it carries the pages that already exist.
 * Later work (Online Now, Share Event, Invite a Cohort, Member Events, Find
 * Events, …) adds its own entry here as each page lands.
 */
export const MENU: NavGroup[] = [
  {
    title: "Members & Chat",
    list: [
      {
        icon: <UsersIcon />,
        title: "Members",
        href: "/users",
        description: "Search, filter and impersonate members.",
      },
      {
        icon: <AutomationIcon />,
        title: "Chat Automations",
        href: "/chat-automations",
        description: "Greet new members and message them in bulk.",
      },
      {
        icon: <MessageSquareIcon />,
        title: "Captured Chats",
        href: "/captured-chats",
        description: "Read and search what members actually wrote back.",
      },
      {
        icon: <OnlineIcon />,
        title: "Online Now",
        href: "/online-now",
        description: "Greet at-risk members while they're on the platform.",
      },
      {
        icon: <RetentionIcon />,
        title: "Retention",
        href: "/retention",
        description: "Route drifting members (Detected / Dormant) to the right save.",
      },
      {
        icon: <CancellationIcon />,
        title: "Cancellations",
        href: "/cancellations",
        description: "Win back members whose membership or trial is about to lapse.",
      },
      {
        icon: <NewMemberIcon />,
        title: "New Members",
        href: "/new-members",
        description: "Welcome new joiners with a check-in written from their signup answers.",
      },
    ],
  },
  {
    title: "Event Invites",
    list: [
      {
        icon: <NewMemberIcon />,
        title: "Invite Campaigns",
        href: "/invite-campaigns",
        description: "Invite the members waiting in each campaign to the next event in their zone.",
      },
      {
        icon: <Share2Icon />,
        title: "Share Event",
        href: "/share-event",
        description: "Send a shared-event heads-up to a list of members.",
      },
      {
        icon: <TargetIcon />,
        title: "Invite a Cohort",
        href: "/invite-cohort",
        description: "Build a group by risk, tenure and zone, and invite them to an event.",
      },
      {
        icon: <UsersIcon />,
        title: "Member Events",
        href: "/member-events",
        description: "Paste an event; see the members most likely to want it.",
      },
      {
        icon: <SearchIcon />,
        title: "Find Events",
        href: "/find-events",
        description: "Look up a member; see the events she'd enjoy.",
      },
    ],
  },
  {
    title: "Insights",
    list: [
      {
        icon: <ImpactIcon />,
        title: "Outreach Impact",
        href: "/outreach-impact",
        description: "What every programme has reached — and whether those members took action.",
      },
      {
        icon: <ReportIcon />,
        title: "Reports",
        href: "/reports",
        description: "Member reports and exports.",
      },
    ],
  },
  {
    title: "Events",
    list: [
      {
        icon: <CalendarIcon />,
        title: "Events",
        href: "/events",
        description: "Browse events, participants and comments.",
      },
    ],
  },
  {
    title: "Content",
    list: [
      {
        icon: <BookOpenIcon />,
        title: "Themes",
        href: "/themes",
        description: "Monthly themes, libraries and drafts.",
      },
      {
        icon: <FileIcon />,
        title: "Topics",
        href: "/topics",
        description: "Conversation topics and their lessons.",
      },
      {
        icon: <ArticleIcon />,
        title: "Articles",
        href: "/articles",
        description: "Published articles and drafts.",
      },
      {
        icon: <ImageIcon />,
        title: "Covers",
        href: "/covers",
        description: "Cover art used across the app.",
      },
      {
        icon: <BookIcon />,
        title: "Zine Covers",
        href: "/zine-covers",
        description: "Covers for the zine.",
      },
      {
        icon: <MoodIcon />,
        title: "Illustrations",
        href: "/illustrations",
        description: "The illustration library.",
      },
      {
        icon: <LiveIcon />,
        title: "Live Tables",
        href: "/live-tables",
        description: "Current topic questions and ice breakers.",
      },
    ],
  },
  {
    title: "Site",
    list: [
      {
        icon: <HomeBanners />,
        title: "Home Page",
        href: "/home-page",
        description: "What members land on when they open the app.",
      },
      {
        icon: <LayoutIcon />,
        title: "Banners",
        href: "/sliders",
        description: "Slides in the home page banner.",
      },
      {
        icon: <FilmIcon />,
        title: "Videos",
        href: "/videos",
        description: "Video sets shown on the home page.",
      },
      {
        icon: <EditNotificationsOutlinedIcon />,
        title: "Popup Notifications",
        href: "/popup-notifications",
        description: "One-off popups shown inside the app.",
      },
      {
        icon: <ToolIcon />,
        title: "Maintenance",
        href: "/maintenance",
        description: "Maintenance mode and daily hooks.",
      },
    ],
  },
  {
    title: "Account",
    list: [
      {
        icon: <UserIcon />,
        title: "Account settings",
        href: "/account",
        description: "Your password and trusted devices.",
      },
    ],
  },
];
